import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

export type TimerProps = {
  endTime?: number;
  length: number;
  onFinish: () => void;
  warnAtTimeLeft?: number;
  dangerAtTimeLeft?: number;
  /** Optional content shown on the left of the time label row (e.g. stall counter). */
  leading?: ReactNode;
  /** Tighter layout for in-match chrome on short viewports. */
  compact?: boolean;
};

export default function Timer({
  endTime,
  length,
  onFinish,
  warnAtTimeLeft = 0,
  dangerAtTimeLeft = 0,
  leading,
  compact = false,
}: TimerProps) {
  const [timeLeft, setTimeLeft] = useState(
    endTime ? Math.ceil((endTime - Date.now()) / 1000) : 0
  );
  /** When the deadline jumps up (new turn), snap the bar instead of easing upward. */
  const [snapBar, setSnapBar] = useState(false);
  const prevEndRef = useRef<number | undefined>(endTime);
  const prevTimeRef = useRef(timeLeft);

  useEffect(() => {
    if (!endTime) return;

    const next = Math.ceil((endTime - Date.now()) / 1000);
    const endChanged = prevEndRef.current !== endTime;
    const jumpedUp = next > prevTimeRef.current;
    if (endChanged && jumpedUp) {
      setSnapBar(true);
    }
    prevEndRef.current = endTime;
    prevTimeRef.current = next;
    setTimeLeft(next);

    const intervalId = setInterval(() => {
      const tick = Math.ceil((endTime - Date.now()) / 1000);
      if (tick >= 0) {
        prevTimeRef.current = tick;
        setTimeLeft(tick);
        setSnapBar(false);
      } else {
        prevTimeRef.current = 0;
        setTimeLeft(0);
        onFinish();
        clearInterval(intervalId);
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [endTime, onFinish]);

  useEffect(() => {
    if (!snapBar) return;
    const id = window.requestAnimationFrame(() => setSnapBar(false));
    return () => window.cancelAnimationFrame(id);
  }, [snapBar, timeLeft]);

  if (!endTime) return null;

  const urgent = dangerAtTimeLeft > 0 && timeLeft > 0 && timeLeft <= dangerAtTimeLeft;
  const denom = Math.max(length - 1, 1);
  const widthPct =
    timeLeft <= 0 ? 0 : Math.min(((timeLeft - 1) / denom) * 100, 100);

  return (
    <div className={`w-full ${compact ? 'mt-1 space-y-1' : 'space-y-1.5'}`}>
      <div
        className={`flex items-center justify-between gap-3 font-semibold uppercase tracking-wide ${
          compact ? 'text-[10px] sm:text-xs' : 'text-xs md:text-sm'
        }`}
      >
        <div className="min-w-0 flex-1">{leading}</div>
        <div
          className={`shrink-0 tabular-nums ${
            urgent ? 'animate-bk-urgent text-bk-danger-bright' : 'text-bk-muted'
          }`}
        >
          {compact ? '' : 'Time: '}
          {!timeLeft || timeLeft <= 0 ? 0 : timeLeft}
        </div>
      </div>
      <div className={`${compact ? 'h-1.5' : 'h-2'} w-full border border-bk-border bg-bk-bg`}>
        <div
          className={`h-full ease-linear ${
            urgent
              ? 'bg-bk-danger animate-bk-pulse-soft'
              : warnAtTimeLeft >= timeLeft
                ? 'bg-bk-gold'
                : 'bg-emerald-600'
          } ${snapBar ? 'duration-0' : 'duration-1000'} transition-[width]`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
    </div>
  );
}
